import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import DeleteModal from '@/components/Modals/DeleteModal';
import GlobalIcon from '@/images/Global.svg';
import Trash from '@/images/Trash.svg';
import { TriggerWebhook } from '@d-id/types';
import { theme } from '@de-id/ui-components';
import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import { useState } from 'react';
import { useWebhookComponentContext } from '../contex';

const WebhookItemContainer = styled.div`
    display: flex;
    gap: 12px;
    padding: 8px;
    align-items: center;
    border-radius: 8px;
    background: ${theme.colors.black[50]};

    color: ${theme.colors.black[900]};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    cursor: pointer;
    transition: background-color 0.2s ease-in-out;

    :hover {
        background-color: ${theme.colors.black[100]};
    }
`;

const GlobeContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    border: 1px solid ${theme.colors.black[300]};
    background: #fff;
`;

const UrlText = styled.span`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-all;
    flex: 1;
`;

const DeleteButton = styled(IconButton)`
    margin-left: auto;
    border-radius: 8px;
    border: 1px solid ${theme.colors.black[300]};
    background: #fff;
    width: 32px;
    height: 32px;
`;

interface Props {
    webhook: TriggerWebhook;
    index: number;
}

export const WebhookItem = ({ webhook, index }: Props) => {
    const { onWebhookRemove, onWebhookEdit } = useWebhookComponentContext();
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

    const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setDeleteConfirmationOpen(true);
    };

    return (
        <>
            <WebhookItemContainer onClick={() => onWebhookEdit(webhook, index)}>
                <GlobeContainer>
                    <MaskedIcon src={GlobalIcon} size="20px" color={theme.colors.secondary.main} />
                </GlobeContainer>

                <Tooltip
                    title={webhook.url}
                    placement="top"
                    slotProps={{
                        tooltip: { sx: { backgroundColor: theme.colors.secondary[900], padding: '8px', fontSize: '14px' } },
                    }}>
                    <UrlText>{webhook.url}</UrlText>
                </Tooltip>

                <DeleteButton ariaLabel="Delete" onClick={handleDelete}>
                    <MaskedIcon src={Trash} size="16px" color={theme.colors.secondary.main} />
                </DeleteButton>
            </WebhookItemContainer>

            <DeleteModal
                itemType="webhook"
                mode="square"
                open={deleteConfirmationOpen}
                isLoading={false}
                onDelete={async () => await onWebhookRemove(index)}
                onClose={() => setDeleteConfirmationOpen(false)}
                text={{
                    title: 'Confirm delete',
                    content: `Are you sure you want to delete this Webhook endpoint? This action cannot be undone.`,
                    cta: 'Delete endpoint',
                }}
            />
        </>
    );
};
